import { Badge, IconButton, Tooltip } from '@mui/material';
import { DrawPolygonByDraggingMode, ViewMode } from '@nebula.gl/edit-modes';
import { EditableGeoJsonLayer } from '@nebula.gl/layers';
import { FeatureCollection } from '@turf/helpers';
import clsx from 'clsx';
import { useCustomSnackbar } from 'components/NotificationSnackbars';
import { EditableGeoJsonLayerProps } from 'components/maps/BaseMap';
import { KineticMapContext, MapContext } from 'components/maps/MapContext';
import { MapMarkerContext } from 'components/maps/MapMarkerContext';
import { RGBAColor } from 'deck.gl';
import { useHotKey } from 'kineticUtils/hooks';
import difference from 'lodash/difference';
import uniq from 'lodash/uniq';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LassoIcon from '../../../../assets/map/Lasso.png';
import { MapTool } from '../const';
import useLayersToolbarStyles from '../styles';
import { LassoToolProps } from './types';

const LASSO_LINE: RGBAColor = [51, 136, 255];
const LASSO_FILL: RGBAColor = [51, 136, 255, 0x60];
const LASSO_FILL_SELECTED: RGBAColor = [51, 136, 255, 0x90];

export const DeckLassoTool: React.FunctionComponent<LassoToolProps> = () => {
  const { t } = useTranslation('app');
  const classes = useLayersToolbarStyles();
  const {
    setEntities,
    removeEntities,
    selectedEntities,
    selectedEntitiesCount,
    activeMapTool,
    intersectsWithExtraDeckGlData,
    setExtraDeckGlLayers,
    setActiveMapTool,
    setAdditionalMapContextMenuItems,
  } = useContext(MapContext);
  const { deckRef, mapRef } = useContext(KineticMapContext);
  const { intersectsWithData } = useContext(MapMarkerContext);
  const { enqueueCustomSnackbar } = useCustomSnackbar();

  const isActive = activeMapTool === MapTool.LASSO;
  // This will hold our geojson lassos
  const [featureCollection, setFeatureCollection] = useState<FeatureCollection>({
    type: 'FeatureCollection',
    features: [],
  });

  const [selectedIds, setSelectedIds] = useState<string[][]>([]);
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState<number[]>([]);

  const contextMenuItemText = t('removeSelectedArea');

  const removeAdditionalMapContextMenuItem = useCallback(() => {
    setAdditionalMapContextMenuItems((prev) =>
      prev.filter((menuItem) => menuItem.text !== contextMenuItemText)
    );
  }, [contextMenuItemText, setAdditionalMapContextMenuItems]);

  useEffect(() => {
    if (selectedEntities.size === 0) {
      setSelectedIds([]);
      setSelectedFeatureIndex([]);
      setFeatureCollection({
        type: 'FeatureCollection',
        features: [],
      });
      removeAdditionalMapContextMenuItem();
    }
  }, [selectedEntities.size, removeAdditionalMapContextMenuItem, t]);

  const handleDeleteSelectedFeature = (selectedFeatureIndexes: number[]) => {
    if (selectedFeatureIndexes.length) {
      setFeatureCollection((prev) => {
        const toSave = [...prev.features];
        toSave.splice(selectedFeatureIndexes[0], 1);
        return {
          ...prev,
          features: toSave,
        };
      });

      const saveSelectedIds = [...selectedIds];
      const removedIds = saveSelectedIds[selectedFeatureIndexes[0]];
      saveSelectedIds.splice(selectedFeatureIndexes[0], 1);
      const differenceIds = difference(removedIds, saveSelectedIds.flat());
      removeEntities(differenceIds);
      setSelectedIds(saveSelectedIds);

      setSelectedFeatureIndex([]);
    }
    removeAdditionalMapContextMenuItem();
  };

  const handleDeleteSelected = () => handleDeleteSelectedFeature(selectedFeatureIndex);
  useHotKey({ key: 'l', alt: true, ctrl: true, callback: () => setActiveMapTool(MapTool.LASSO) });
  useHotKey({ key: 'd', ctrl: true, callback: handleDeleteSelected });
  useHotKey({ key: 'delete', ctrl: false, callback: handleDeleteSelected });
  const getRemoveItem = (selectedFeatureIndexes: number[]) => ({
    text: contextMenuItemText,
    callback: () => handleDeleteSelectedFeature(selectedFeatureIndexes),
  });

  // Disable tool if another tool or context menu is active

  useEffect(() => {
    setExtraDeckGlLayers((prev) => {
      const toSave = [...prev];
      const layer = new EditableGeoJsonLayer({
        id: 'lasso-layer',
        data: featureCollection,
        modeConfig: {
          throttleMs: 100,
          dragToDraw: true,
        },
        pickingRadius: 20,
        selectedFeatureIndexes: selectedFeatureIndex,
        mode: !isActive ? ViewMode : DrawPolygonByDraggingMode,
        onEdit: (data) => {
          if (data.editType === 'addFeature') {
            const editIndex = data.editContext.featureIndexes[0];
            const pickedIds = intersectsWithData(data.updatedData.features[editIndex].geometry);
            const pickedExtraDeckGlLayerIds = intersectsWithExtraDeckGlData(
              data.updatedData.features[editIndex].geometry
            );

            if (!pickedIds.length && !pickedExtraDeckGlLayerIds.length) {
              enqueueCustomSnackbar(t('noSelectionsFound'), {
                variant: 'warning',
                autoHideDuration: 3000,
              });
              return;
            }

            setSelectedIds((prev) => {
              const toSave = [...prev];
              toSave[editIndex] = [...pickedIds, ...pickedExtraDeckGlLayerIds];
              const entities = uniq(Object.values(toSave).flat());

              setEntities(entities);
              return toSave;
            });

            setFeatureCollection(data.updatedData);
          }
        },
        // TS is the build isn't picking up the correct type
        onClick: (pick, event: any) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          event.srcEvent.stopPropagation();
          event.srcEvent.preventDefault();

          const foundIndex = featureCollection.features.findIndex((obj) => obj === pick.object);
          if (foundIndex === -1 || (foundIndex === selectedFeatureIndex[0] && !event.rightButton)) {
            setSelectedFeatureIndex([]);
            setAdditionalMapContextMenuItems((prev) =>
              prev.filter((item) => item.text !== contextMenuItemText)
            );
            return;
          }
          setSelectedFeatureIndex([foundIndex]);
          setAdditionalMapContextMenuItems((prev) => {
            const updatedItems = prev.filter((item) => item.text !== contextMenuItemText);
            return [...updatedItems, getRemoveItem([foundIndex])];
          });
        },
        getLineColor: LASSO_LINE,
        getFillColor: (_feature, isSelected, _mode) =>
          isSelected ? LASSO_FILL_SELECTED : LASSO_FILL,
        getTentativeLineColor: LASSO_LINE,
        getTentativeFillColor: LASSO_FILL_SELECTED,
        getEditHandlePointColor: LASSO_LINE,
        order: 1,
      } as EditableGeoJsonLayerProps);

      const index = toSave.findIndex((layer) => layer.id === 'lasso-layer');
      if (index > -1) {
        toSave.splice(index, 1);
      }
      toSave.push(layer);

      return toSave;
    });
    // don't watch enqueueCustomSnackbar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isActive,
    featureCollection,
    selectedFeatureIndex,
    setExtraDeckGlLayers,
    mapRef,
    deckRef,
    setEntities,
    intersectsWithData,
    t,
  ]);

  return (
    <>
      <Tooltip title={t('selectionTool')}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setActiveMapTool(MapTool.LASSO);
          }}
          className={clsx(classes.button, { [classes.buttonActive]: isActive })}
          sx={{ padding: '9px' }}
        >
          <Badge badgeContent={selectedEntitiesCount} color='primary' variant='dot'>
            <img className={classes.lassoIcon} src={LassoIcon} alt='lasso tool' />
          </Badge>
        </IconButton>
      </Tooltip>
    </>
  );
};

export default memo(DeckLassoTool);
